import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { InformationCircleIcon } from "@heroicons/react/16/solid";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { useLoading } from "../contexts/LoadingContext";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import OnDemandSigningContainerList from "../components/OnDemandSigningContainerList";
import {
  createOnDemandSigningContainer,
  getBulkUploadTemplates,
} from "../api/documents";
import { hasDocumentTypeAccess } from "../utils/permissions";
import { canUseOnDemandSigning } from "../utils/organizationFeatures";
import { Button, Card, DataGrid, Dropdown, Input, Tabs } from "../components/ui";

const FORM_ID = "ondemand-signing-form";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUB_TABS = {
  list: "list",
  create: "create",
};

const OnDemandSigning = () => {
  const { organization, permissions } = useAuth();
  const { setHeaderContent } = usePageHeader();
  const { showLoader, hideLoader } = useLoading();
  const navigate = useNavigate();

  const [activeSubTab, setActiveSubTab] = useState(SUB_TABS.list);
  const [listRefreshVersion, setListRefreshVersion] = useState(0);

  const [containerName, setContainerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [csvError, setCsvError] = useState("");
  const [recipientPage, setRecipientPage] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [bulkUploadTemplates, setBulkUploadTemplates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFeatureAllowed = canUseOnDemandSigning(organization);

  const filteredTemplates = useMemo(() => {
    if (!permissions || !permissions.templates) {
      return bulkUploadTemplates;
    }
    return bulkUploadTemplates.filter((template) => {
      const documentType = template.documentType || "";
      return hasDocumentTypeAccess(permissions, documentType);
    });
  }, [bulkUploadTemplates, permissions]);

  const ITEMS_PER_PAGE = 50;
  const recipientTotalPages = Math.ceil(recipients.length / ITEMS_PER_PAGE);
  const recipientStartIndex = (recipientPage - 1) * ITEMS_PER_PAGE;
  const recipientEndIndex = recipientStartIndex + ITEMS_PER_PAGE;
  const currentRecipients = recipients.slice(
    recipientStartIndex,
    recipientEndIndex,
  );

  useEffect(() => {
    if (!isFeatureAllowed) return;
    setHeaderContent(
      <div>
        <BreadCrumb
          items={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "On-demand Signing" },
          ]}
          className="mb-2"
        />
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
          On-demand Signing
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl mt-1">
          View signing containers or create a new one with a template and
          recipient list.
        </p>
      </div>,
    );
    return () => setHeaderContent(null);
  }, [isFeatureAllowed, setHeaderContent]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await getBulkUploadTemplates();
        setBulkUploadTemplates(response);
      } catch {
        toast.error("Failed to load templates");
      }
    };
    if (isFeatureAllowed) load();
  }, [isFeatureAllowed]);

  const clearCsvAndRecipients = () => {
    setCsvFile(null);
    setRecipients([]);
    setCsvError("");
    setRecipientPage(1);
    const fileInput = document.getElementById("ondemandCsvFile");
    if (fileInput) fileInput.value = "";
  };

  const handleFileUpload = (event) => {
    setCsvError("");
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setCsvError("Please upload a valid CSV file");
      return;
    }

    setCsvFile(file);
    setIsProcessing(true);
    setProcessingProgress(0);
    setRecipientPage(1);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split("\n");

        if (lines.length < 2) {
          setCsvError(
            "CSV file must contain at least a header row and one data row",
          );
          setIsProcessing(false);
          return;
        }

        const headers = lines[0]
          .split(",")
          .map((h) => h.trim().toLowerCase().replace(/\s+/g, " "));
        const findCol = (candidates) => {
          for (const c of candidates) {
            const i = headers.indexOf(c);
            if (i !== -1) return i;
          }
          return -1;
        };
        const nameIndex = findCol(["name"]);
        const emailIndex = findCol(["email"]);
        const siNoIndex = findCol([
          "si.no",
          "si no",
          "sino",
          "sl.no",
          "sl no",
          "serial no",
          "serial number",
          "si_no",
          "s.no",
          "s no",
        ]);
        const unitIndex = findCol(["unit"]);
        const departmentIndex = findCol(["department", "dept"]);

        if (nameIndex === -1 || emailIndex === -1) {
          setCsvError(
            "Missing required columns: Name and Email are required in the header row.",
          );
          setIsProcessing(false);
          return;
        }

        const colIndexes = [
          nameIndex,
          emailIndex,
          siNoIndex,
          unitIndex,
          departmentIndex,
        ].filter((i) => i >= 0);
        const requiredCols =
          colIndexes.length > 0 ? Math.max(...colIndexes) + 1 : 2;

        const processChunk = (startLine, chunkSize) => {
          const endLine = Math.min(startLine + chunkSize, lines.length);
          const chunkRecipients = [];

          for (let i = startLine; i < endLine; i++) {
            if (!lines[i].trim()) continue;

            const values = lines[i].split(",").map((v) => v.trim());
            if (values.length < requiredCols) {
              setCsvError(
                `Row ${i + 1} has insufficient columns. Each row needs Name and Email.`,
              );
              setIsProcessing(false);
              return null;
            }

            const name = values[nameIndex] || "";
            const email = values[emailIndex] || "";

            if (!name) {
              setCsvError(`Missing name in row ${i + 1}.`);
              setIsProcessing(false);
              return null;
            }
            if (!email) {
              setCsvError(`Missing email in row ${i + 1}.`);
              setIsProcessing(false);
              return null;
            }
            if (!EMAIL_REGEX.test(email)) {
              setCsvError(`Invalid email format in row ${i + 1}: ${email}`);
              setIsProcessing(false);
              return null;
            }

            const siNo =
              siNoIndex >= 0 ? (values[siNoIndex] || "").trim() : "";
            const unit =
              unitIndex >= 0 ? (values[unitIndex] || "").trim() : "";
            const department =
              departmentIndex >= 0
                ? (values[departmentIndex] || "").trim()
                : "";

            chunkRecipients.push({ name, email, siNo, unit, department });
          }

          return chunkRecipients;
        };

        const CHUNK_SIZE = 100;
        let allRecipients = [];
        let currentLine = 1;

        const processNextChunk = () => {
          const chunk = processChunk(currentLine, CHUNK_SIZE);
          if (chunk === null) return;

          allRecipients = allRecipients.concat(chunk);
          currentLine += CHUNK_SIZE;
          const progress = Math.min((currentLine / lines.length) * 100, 100);
          setProcessingProgress(progress);

          if (currentLine < lines.length) {
            setTimeout(processNextChunk, 0);
          } else {
            if (allRecipients.length === 0) {
              setCsvError("No valid recipient data found in CSV file");
            } else {
              setRecipients(allRecipients);
            }
            setIsProcessing(false);
            setProcessingProgress(0);
          }
        };

        processNextChunk();
      } catch {
        setCsvError("Error parsing CSV file. Please check the format.");
        setIsProcessing(false);
        setProcessingProgress(0);
      }
    };
    reader.readAsText(file);
  };

  const downloadSampleCsv = () => {
    const csvContent =
      "SI.No,Name,Email,Unit,Department\n1,Jane Example,jane@example.com,HQ,HR\n2,Alex Sample,alex@example.com,Branch A,Finance";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ondemand_signing_recipients.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleRecipientPageChange = (page) => setRecipientPage(page);

  const templateOptions = filteredTemplates.map((t) => ({
    value: t._id,
    label: t.name,
  }));

  const recipientsColumns = [
    {
      key: "siNo",
      label: "SI.No",
      sortable: false,
      render: (v) =>
        v ? <span className="tabular-nums">{v}</span> : (
          <span className="text-gray-400">—</span>
        ),
    },
    { key: "name", label: "Name", sortable: false },
    { key: "email", label: "Email", sortable: false },
    {
      key: "unit",
      label: "Unit",
      sortable: false,
      render: (v) => v || <span className="text-gray-400">—</span>,
    },
    {
      key: "department",
      label: "Department",
      sortable: false,
      render: (v) => v || <span className="text-gray-400">—</span>,
    },
  ];

  const canSubmit =
    containerName.trim() &&
    ownerEmail.trim() &&
    EMAIL_REGEX.test(ownerEmail.trim()) &&
    selectedTemplate &&
    recipients.length > 0 &&
    !csvError &&
    !isProcessing &&
    !isSubmitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    showLoader();
    try {
      await createOnDemandSigningContainer({
        templateId: selectedTemplate,
        containerName: containerName.trim(),
        ownerEmail: ownerEmail.trim(),
        recipients,
      });
      toast.success(
        "Container created. Recipients are importing in the background.",
      );
      setContainerName("");
      setOwnerEmail("");
      setSelectedTemplate("");
      setRecipients([]);
      setCsvFile(null);
      setCsvError("");
      const fileInput = document.getElementById("ondemandCsvFile");
      if (fileInput) fileInput.value = "";
      setListRefreshVersion((v) => v + 1);
      setActiveSubTab(SUB_TABS.list);
    } catch (err) {
      const msg =
        typeof err === "object" && err?.message
          ? err.message
          : "Failed to create container";
      toast.error(msg);
    } finally {
      hideLoader();
      setIsSubmitting(false);
    }
  };

  const renderRecipientPagination = () => {
    if (recipientTotalPages <= 1) return null;
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      recipientPage - Math.floor(maxVisiblePages / 2),
    );
    let endPage = Math.min(recipientTotalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <p className="text-sm text-gray-700">
          Showing <span className="font-medium">{recipientStartIndex + 1}</span>{" "}
          to{" "}
          <span className="font-medium">
            {Math.min(recipientEndIndex, recipients.length)}
          </span>{" "}
          of <span className="font-medium">{recipients.length}</span> recipients
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleRecipientPageChange(recipientPage - 1)}
            disabled={recipientPage === 1}
            className="rounded-l-lg rounded-r-none px-2"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          {pages.map((page) => (
            <Button
              key={page}
              variant={page === recipientPage ? "primary" : "outline"}
              size="sm"
              onClick={() => handleRecipientPageChange(page)}
              className="rounded-lg px-3"
            >
              {page}
            </Button>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleRecipientPageChange(recipientPage + 1)}
            disabled={recipientPage === recipientTotalPages}
            className="rounded-r-lg rounded-l-none px-2"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  if (!isFeatureAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center" padding="lg">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-6">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h1 className="font-heading text-xl font-bold text-gray-900 mb-3">
            Feature Not Available
          </h1>
          <p className="text-gray-600 mb-6 leading-relaxed">
            On-demand Signing is not enabled for this organization.
          </p>
          <Button
            onClick={() => navigate("/dashboard")}
            fullWidth
            size="lg"
          >
            Back to dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const onDemandTabs = [
    { value: SUB_TABS.list, label: "Containers" },
    { value: SUB_TABS.create, label: "Create container" },
  ];

  return (
    <div>
      <Tabs
        tabs={onDemandTabs}
        value={activeSubTab}
        onChange={setActiveSubTab}
      />
      <div className="bg-white overflow-hidden">
        <div className="px-4 py-6">
          {activeSubTab === SUB_TABS.list && (
            <OnDemandSigningContainerList refreshVersion={listRefreshVersion} />
          )}

          {activeSubTab === SUB_TABS.create && (
            <div className="max-w-6xl">
                  <div className="mb-6">
                    <h2 className="font-heading text-lg font-semibold text-gray-900">
                      Create container
                    </h2>
                    <p className="text-sm text-gray-600 mt-1 max-w-3xl">
                      Name the container, choose who receives the owner portal
                      link, pick a single-signer template, then upload your
                      recipient list as CSV. Import runs in the background after
                      you create.
                    </p>
                  </div>

                  <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid gap-6 lg:grid-cols-2">
                      <Card padding="lg" className="h-full">
                        <h3 className="font-heading text-sm font-semibold text-gray-900 mb-4">
                          Container details
                        </h3>
                        <div className="space-y-4">
                          <Input
                            label="Container name"
                            value={containerName}
                            onChange={(e) => setContainerName(e.target.value)}
                            placeholder="e.g. Q1 vendor packets"
                            required
                          />
                          <Input
                            label="Container owner email"
                            type="email"
                            value={ownerEmail}
                            onChange={(e) => setOwnerEmail(e.target.value)}
                            placeholder="owner@company.com"
                            required
                          />
                          <p className="text-xs text-gray-500 -mt-2">
                            This person gets the link to open the signer list
                            and distribute signing links.
                          </p>
                        </div>
                      </Card>

                      <Card padding="lg" className="h-full">
                        <h3 className="font-heading text-sm font-semibold text-gray-900 mb-4">
                          Template
                        </h3>
                        <Dropdown
                          label="Select template"
                          options={templateOptions}
                          value={selectedTemplate}
                          onChange={(val) => setSelectedTemplate(val)}
                          placeholder="Choose a template…"
                        />
                        <div className="info-text bg-blue-50 rounded-lg p-2 mt-3 flex items-start gap-2">
                          <InformationCircleIcon className="h-5 w-5 text-blue-800 shrink-0 mt-0.5" />
                          <p className="text-sm text-blue-800">
                            Same rules as bulk send: templates with exactly one
                            signer can be used.
                          </p>
                        </div>
                      </Card>
                    </div>

                    <Card padding="lg">
                      <h3 className="font-heading text-sm font-semibold text-gray-900 mb-4">
                        Recipients (CSV)
                      </h3>
                      <label
                        htmlFor="ondemandCsvFile"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Upload CSV
                      </label>
                      <input
                        type="file"
                        id="ondemandCsvFile"
                        accept=".csv,text/csv"
                        onChange={handleFileUpload}
                        className="block w-full px-4 py-3 border border-gray-300 hover:border-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg text-gray-900 text-sm transition-colors duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                      />
                      {csvFile && (
                        <p className="text-xs text-gray-500 mt-1">
                          Selected: {csvFile.name}
                        </p>
                      )}
                      {csvError && (
                        <p className="mt-2 text-sm text-red-600" role="alert">
                          {csvError}
                        </p>
                      )}
                      {isProcessing && (
                        <div className="mt-2">
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                            <span className="text-sm text-gray-600">
                              Processing CSV…
                            </span>
                          </div>
                          <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full transition-all duration-300"
                              style={{ width: `${processingProgress}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/90 p-4">
                        <p className="text-sm font-medium text-gray-900 mb-2">
                          CSV column reference
                        </p>
                        <ul className="text-sm text-gray-700 space-y-2">
                          <li>
                            <span className="font-medium text-gray-900">
                              Required:
                            </span>{" "}
                            <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200">
                              Name
                            </code>
                            ,{" "}
                            <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200">
                              Email
                            </code>
                          </li>
                          <li>
                            <span className="font-medium text-gray-900">
                              Optional:
                            </span>{" "}
                            <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200">
                              SI.No
                            </code>{" "}
                            (or Sl no, Serial no, …),{" "}
                            <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200">
                              Unit
                            </code>
                            ,{" "}
                            <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200">
                              Department
                            </code>{" "}
                            /{" "}
                            <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200">
                              Dept
                            </code>
                          </li>
                        </ul>
                        <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                          First row must be headers matching the names above
                          (letter case ignored). Use simple CSV without commas
                          inside cells. Download the sample file for a valid
                          layout.
                        </p>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={downloadSampleCsv}
                          className="text-primary hover:text-primary-dark shrink-0"
                        >
                          Download sample CSV
                        </Button>
                        {(csvFile || recipients.length > 0) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            leftIcon={TrashIcon}
                            onClick={clearCsvAndRecipients}
                            disabled={isProcessing || isSubmitting}
                          >
                            Clear upload
                          </Button>
                        )}
                      </div>
                    </Card>

                    {recipients.length > 0 && (
                      <Card padding="none" className="overflow-hidden">
                        <div className="px-4 pt-4 pb-2 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100">
                          <span className="text-sm font-medium text-gray-700">
                            Preview — {recipients.length.toLocaleString()}{" "}
                            {recipients.length === 1
                              ? "recipient"
                              : "recipients"}
                          </span>
                          <span className="text-sm text-gray-500 tabular-nums">
                            Rows {recipientStartIndex + 1}–
                            {Math.min(recipientEndIndex, recipients.length)} of{" "}
                            {recipients.length.toLocaleString()}
                          </span>
                        </div>
                        <div className="max-h-[28rem] overflow-auto">
                          <div className="min-w-[52rem]">
                            <DataGrid
                              columns={recipientsColumns}
                              data={currentRecipients.map((r, i) => ({
                                ...r,
                                id: `r-${recipientStartIndex + i}`,
                              }))}
                              headerVariant="white"
                              emptyMessage="No recipients"
                            />
                          </div>
                        </div>
                        {renderRecipientPagination()}
                      </Card>
                    )}

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={!canSubmit}
                        leftIcon={isSubmitting ? undefined : PlusIcon}
                      >
                        {isSubmitting
                          ? "Creating…"
                          : `Create container (${recipients.length.toLocaleString()} recipients)`}
                      </Button>
                    </div>
                  </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnDemandSigning;
